/**
 * Push to Play — toolbar-integrated component.
 *
 * Shared between Editor and Web apps via editor-core.
 * Uses EngineService push/* methods through the PlatformService abstraction.
 *
 * When disconnected: shows a "推送" button that opens a device discovery dropdown.
 * When connected & paired: shows inline toolbar controls (push, sync toggle, device info).
 */

import type { DiscoveredDevice } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import {
    Check,
    ChevronDown,
    Loader2,
    Monitor,
    Radio,
    RefreshCw,
    Search,
    Send,
    Smartphone,
    Unplug,
    Wifi,
    X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePushSender } from "../hooks/usePushSender";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { usePushClientStore } from "../store/push-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { frontendLog } from "../utils/frontend-logger";

function setEditorStatusMessage(message: string): void {
    useEditorStore.getState().setStatusMessage(message);
}

function getFocusedTabContent(): string {
    const state = useEditorStore.getState();
    const focusedTabId = state.getFocusedTabId();
    const tab = state.tabs.find((item) => item.id === focusedTabId);
    return tab?.content ?? "";
}

export function PushButton() {
    const t = useT("editor");
    const panelOpen = usePushClientStore((s) => s.panelOpen);
    const setPanelOpen = usePushClientStore((s) => s.setPanelOpen);
    const connected = usePushClientStore((s) => s.connected);
    const paired = usePushClientStore((s) => s.paired);
    const syncing = usePushClientStore((s) => s.syncing);
    const lastPushStatus = usePushClientStore((s) => s.lastPushStatus);
    const remoteDeviceName = usePushClientStore((s) => s.remoteDeviceName);

    const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const {
        scanDevices: scanDevicesFn,
        connect: connectFn,
        disconnect: disconnectFn,
        requestPairing: requestPairingFn,
        sendPairingCode: sendPairingCodeFn,
        pushDocument: pushDocumentFn,
        startSync: startSyncFn,
        syncUpdate: syncUpdateFn,
        stopSync: stopSyncFn,
    } = usePushSender();

    // Debounced sync update on content change.
    // Keep this out of render subscriptions so normal typing does not re-render toolbar chrome.
    useEffect(() => {
        if (!syncing || !paired || !connected) {
            if (syncTimerRef.current) {
                clearTimeout(syncTimerRef.current);
                syncTimerRef.current = null;
            }
            return;
        }

        let lastContent = getFocusedTabContent();
        const scheduleSyncUpdate = () => {
            if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
            syncTimerRef.current = setTimeout(() => {
                syncTimerRef.current = null;
                syncUpdateFn().catch((err) =>
                    frontendLog.error("Sync update failed", err, { source: "ui" }),
                );
            }, 500);
        };

        scheduleSyncUpdate();
        const unsubscribe = useEditorStore.subscribe(() => {
            const nextContent = getFocusedTabContent();
            if (nextContent === lastContent) return;
            lastContent = nextContent;
            scheduleSyncUpdate();
        });

        return () => {
            unsubscribe();
            if (syncTimerRef.current) {
                clearTimeout(syncTimerRef.current);
                syncTimerRef.current = null;
            }
        };
    }, [syncing, paired, connected, syncUpdateFn]);

    const isConnectedAndPaired = connected && paired;

    // ────────────────────────────────────────────
    // Connected & Paired: inline toolbar controls
    // ────────────────────────────────────────────
    if (isConnectedAndPaired) {
        return (
            <div
                className={cn(
                    "relative flex items-center rounded-md border overflow-hidden",
                    syncing ? "border-primary/20" : "theme-accent-border",
                )}
                style={!syncing ? getThemeAccentStyle("success") : undefined}
            >
                {/* Push document button */}
                <button
                    type="button"
                    onClick={() =>
                        pushDocumentFn().then(() => {
                            setEditorStatusMessage(t("push.status.documentPushed"));
                        }).catch((err) => {
                            frontendLog.error("Push document failed", err, { source: "ui" });
                            setEditorStatusMessage(t("push.status.pushFailed"));
                        })
                    }
                    disabled={lastPushStatus === "sending"}
                    className={cn(
                        "flex items-center gap-1.5 pl-2.5 pr-2 py-1 text-xs font-medium transition-all",
                        lastPushStatus === "sending"
                            ? "bg-primary/10 text-primary"
                        : lastPushStatus === "success"
                                ? "theme-accent-chip"
                                : "theme-accent-button",
                    )}
                    style={
                        lastPushStatus === "sending"
                            ? undefined
                            : getThemeAccentStyle("success")
                    }
                    title={t("push.pushCurrentDocument")}
                >
                    {lastPushStatus === "sending" ? (
                        <Loader2 size={13} className="animate-spin" />
                    ) : lastPushStatus === "success" ? (
                        <Check size={13} />
                    ) : (
                        <Send size={13} />
                    )}
                    <span className="hidden sm:inline">
                        {lastPushStatus === "sending"
                            ? t("push.sending")
                            : lastPushStatus === "success"
                                ? t("push.sent")
                                : t("push.push")}
                    </span>
                </button>

                {/* Divider */}
                <div
                    className={cn(
                        "w-px self-stretch",
                        syncing ? "bg-primary/20" : "theme-accent-progress-track",
                    )}
                    style={!syncing ? getThemeAccentStyle("success") : undefined}
                />

                {/* Sync toggle button */}
                <button
                    type="button"
                    onClick={() => {
                        if (syncing) {
                            stopSyncFn().then(() => {
                                setEditorStatusMessage(t("push.status.syncStopped"));
                            }).catch((err) => {
                                frontendLog.error("Stop sync failed", err, { source: "ui" });
                                setEditorStatusMessage(t("push.status.stopSyncFailed"));
                            });
                        } else {
                            startSyncFn().then(() => {
                                setEditorStatusMessage(t("push.status.syncStarted"));
                            }).catch((err) => {
                                frontendLog.error("Start sync failed", err, { source: "ui" });
                                setEditorStatusMessage(t("push.status.startSyncFailed"));
                            });
                        }
                    }}
                    className={cn(
                        "flex items-center gap-1 px-2 py-1 text-xs font-medium transition-all",
                        syncing
                            ? "bg-primary/15 text-primary hover:bg-primary/20 active:bg-primary/25"
                            : "theme-accent-button",
                    )}
                    style={!syncing ? getThemeAccentStyle("success") : undefined}
                    title={syncing ? t("push.stopRealtimeSync") : t("push.startRealtimeSync")}
                >
                    <RefreshCw size={12} className={cn(syncing && "animate-spin")} />
                    <span className="hidden sm:inline">
                        {syncing ? t("push.syncing") : t("push.sync")}
                    </span>
                </button>

                {/* Divider */}
                <div
                    className={cn(
                        "w-px self-stretch",
                        syncing ? "bg-primary/20" : "theme-accent-progress-track",
                    )}
                    style={!syncing ? getThemeAccentStyle("success") : undefined}
                />

                {/* Device info / disconnect dropdown trigger */}
                <button
                    type="button"
                    onClick={() => setPanelOpen(!panelOpen)}
                    className={cn(
                        "flex items-center gap-1 px-1.5 py-1 text-xs transition-all",
                        syncing
                            ? "bg-primary/10 text-primary hover:bg-primary/15 active:bg-primary/20"
                            : "theme-accent-button",
                    )}
                    style={!syncing ? getThemeAccentStyle("success") : undefined}
                    title={remoteDeviceName ?? t("push.connectedDevice")}
                >
                    <Radio size={11} />
                    <ChevronDown size={10} />
                </button>

                {/* Minimal connected device dropdown (disconnect only) */}
                {panelOpen ? (
                    <ConnectedDeviceDropdown
                        onClose={() => setPanelOpen(false)}
                        deviceName={remoteDeviceName}
                        syncing={syncing}
                        disconnect={disconnectFn}
                    />
                ) : null}
            </div>
        );
    }

    // ────────────────────────────────────────────
    // Not connected: show discovery button + dropdown
    // ────────────────────────────────────────────
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setPanelOpen(!panelOpen)}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
                title={t("push.toPlay")}
            >
                <Send size={14} />
                <span className="hidden sm:inline">{t("push.push")}</span>
            </button>

            {panelOpen ? (
                <DevicePanel
                    onClose={() => setPanelOpen(false)}
                    actions={{
                        scanDevices: scanDevicesFn,
                        connect: connectFn,
                        disconnect: disconnectFn,
                        requestPairing: requestPairingFn,
                        sendPairingCode: sendPairingCodeFn,
                        pushDocument: pushDocumentFn,
                        startSync: startSyncFn,
                        syncUpdate: syncUpdateFn,
                        stopSync: stopSyncFn,
                    }}
                />
            ) : null}
        </div>
    );
}

/* ═══════════════════════════════════════════════
   Connected device dropdown (minimal — just info + disconnect)
   ═══════════════════════════════════════════════ */

function ConnectedDeviceDropdown({
    onClose,
    deviceName,
    syncing,
    disconnect,
}: {
    onClose: () => void;
    deviceName: string | null;
    syncing: boolean;
    disconnect: () => Promise<void>;
}) {
    const t = useT("editor");
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    return (
        <div
            ref={panelRef}
            className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden"
        >
            <div className="p-2.5 space-y-2">
                {/* Device info */}
                <div
                    className="flex items-center gap-2 p-2 rounded-md theme-accent-banner"
                    style={getThemeAccentStyle("success")}
                >
                    <Radio
                        size={14}
                        className="shrink-0 theme-accent-text"
                        style={getThemeAccentStyle("success")}
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground truncate">
                            {deviceName ?? t("push.playDevice")}
                        </div>
                        <div className="text-[10px]">
                            {syncing ? t("push.realtimeSyncing") : t("push.paired")}
                        </div>
                    </div>
                </div>

                {/* Disconnect button */}
                <button
                    type="button"
                    onClick={() => {
                        disconnect().then(() => {
                            setEditorStatusMessage(t("push.status.disconnected"));
                        }).catch((err) => {
                            frontendLog.error("Disconnect failed", err, { source: "ui" });
                            setEditorStatusMessage(t("push.status.disconnectFailed"));
                        });
                        onClose();
                    }}
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-muted-foreground theme-accent-icon-button transition-colors"
                    style={getThemeAccentStyle("error")}
                >
                    <Unplug size={12} />
                    {t("push.disconnect")}
                </button>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════
   Platform icon
   ═══════════════════════════════════════════════ */

function PlatformIcon({ platform }: { platform: string }) {
    switch (platform) {
        case "ios":
            return <Smartphone size={16} className="text-muted-foreground" />;
        case "android":
            return <Smartphone size={16} className="text-muted-foreground" />;
        default:
            return <Monitor size={16} className="text-muted-foreground" />;
    }
}

/* ═══════════════════════════════════════════════
   4-digit pairing code input
   ═══════════════════════════════════════════════ */

function PairingCodeInput({
    value,
    onChange,
    onComplete,
}: {
    value: string;
    onChange: (v: string) => void;
    onComplete: () => void;
}) {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = value.padEnd(4, "").split("").slice(0, 4);

    const handleDigitChange = useCallback(
        (index: number, char: string) => {
            const digit = char.replace(/\D/g, "").slice(-1);
            const arr = value.padEnd(4, " ").split("").slice(0, 4);
            arr[index] = digit;
            const next = arr.join("").replace(/ /g, "");
            onChange(next);

            if (digit && index < 3) {
                inputRefs.current[index + 1]?.focus();
            }
            if (digit && index === 3 && next.replace(/ /g, "").length === 4) {
                setTimeout(onComplete, 50);
            }
        },
        [value, onChange, onComplete],
    );

    const handleKeyDown = useCallback(
        (index: number, e: React.KeyboardEvent) => {
            if (e.key === "Backspace" && !digits[index]?.trim() && index > 0) {
                inputRefs.current[index - 1]?.focus();
                const arr = value.padEnd(4, " ").split("").slice(0, 4);
                arr[index - 1] = " ";
                onChange(arr.join("").replace(/ /g, ""));
            }
            if (e.key === "Enter") {
                onComplete();
            }
        },
        [digits, value, onChange, onComplete],
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            e.preventDefault();
            const pasted = e.clipboardData
                .getData("text")
                .replace(/\D/g, "")
                .slice(0, 4);
            onChange(pasted);
            const focusIdx = Math.min(pasted.length, 3);
            inputRefs.current[focusIdx]?.focus();
            if (pasted.length === 4) {
                setTimeout(onComplete, 50);
            }
        },
        [onChange, onComplete],
    );

    return (
        <div className="flex items-center justify-center gap-2">
            {[0, 1, 2, 3].map((i) => (
                <input
                    key={i}
                    ref={(el) => {
                        inputRefs.current[i] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digits[i]?.trim() ?? ""}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    autoFocus={i === 0}
                    className="w-10 h-12 text-center text-xl font-bold font-mono rounded-lg border-2 border-border bg-background text-foreground focus:border-primary focus:outline-none transition-colors"
                />
            ))}
        </div>
    );
}

/* ═══════════════════════════════════════════════
   Device discovery & pairing dropdown panel
   (shown only when NOT connected)
   ═══════════════════════════════════════════════ */

function DevicePanel({
    onClose,
    actions,
}: {
    onClose: () => void;
    actions: ReturnType<typeof usePushSender>;
}) {
    const t = useT("editor");
    const connected = usePushClientStore((s) => s.connected);
    const paired = usePushClientStore((s) => s.paired);
    const pairingChallengeDevice = usePushClientStore(
        (s) => s.pairingChallengeDevice,
    );
    const pairingInProgress = usePushClientStore((s) => s.pairingInProgress);
    const discoveredDevices = usePushClientStore((s) => s.discoveredDevices);
    const scanning = usePushClientStore((s) => s.scanning);
    const connectingDeviceId = usePushClientStore((s) => s.connectingDeviceId);
    const setConnectingDeviceId = usePushClientStore(
        (s) => s.setConnectingDeviceId,
    );

    const { scanDevices, connect, sendPairingCode } = actions;

    const [pairingCode, setPairingCode] = useState("");
    const [connectError, setConnectError] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const scannedOnce = useRef(false);

    // Auto-scan when panel opens for the first time (only if no cached devices)
    useEffect(() => {
        if (!connected && !scannedOnce.current && discoveredDevices.length === 0) {
            scannedOnce.current = true;
            scanDevices();
        }
    }, [connected, scanDevices, discoveredDevices.length]);

    // Close panel on outside click
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Auto-close when pairing succeeds (connected+paired)
    useEffect(() => {
        if (connected && paired) {
            const timer = setTimeout(onClose, 600);
            return () => clearTimeout(timer);
        }
    }, [connected, paired, onClose]);

    /** Connect to a discovered device */
    const handleSelectDevice = useCallback(
        async (device: DiscoveredDevice) => {
            setConnectError(null);
            setConnectingDeviceId(device.deviceId);
            try {
                await connect(device.address, device.port);
                setEditorStatusMessage(t("push.status.connectedTo", { name: device.deviceName }));
            } catch (err) {
                setConnectingDeviceId(null);
                const errMsg = err instanceof Error ? err.message : String(err);
                setConnectError(errMsg);
                setEditorStatusMessage(t("push.status.connectionFailed"));
            }
        },
        [connect, setConnectingDeviceId, t],
    );

    const handleSendCode = useCallback(async () => {
        if (pairingCode.length !== 4) return;
        try {
            await sendPairingCode(pairingCode);
            setPairingCode("");
        } catch (err) {
            frontendLog.error("Pairing failed", err, { source: "ui" });
        }
    }, [pairingCode, sendPairingCode]);

    return (
        <div
            ref={panelRef}
            className="absolute right-0 top-full mt-1 w-72 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/50">
                <span className="text-xs font-medium text-foreground">{t("push.toPlay")}</span>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                >
                    <X size={12} />
                </button>
            </div>

            <div className="p-3 space-y-3">
                {connected && pairingChallengeDevice ? (
                    /* ── Pairing code input ── */
                    <div className="py-1">
                        <div className="text-center mb-3">
                            <div className="mx-auto w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-2">
                                <Wifi size={20} className="text-primary" />
                            </div>
                            <p className="text-xs font-medium text-foreground">
                                {pairingChallengeDevice}
                            </p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                                {t("push.enterPairingCode")}
                            </p>
                        </div>

                        <PairingCodeInput
                            value={pairingCode}
                            onChange={setPairingCode}
                            onComplete={handleSendCode}
                        />

                        <button
                            type="button"
                            onClick={handleSendCode}
                            disabled={pairingCode.length !== 4 || pairingInProgress}
                            className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {pairingInProgress ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" />
                                    {t("push.verifying")}
                                </>
                            ) : (
                                t("push.confirmPairing")
                            )}
                        </button>
                    </div>
                ) : (
                    /* ── Device discovery ── */
                    <>
                        {/* Scan header */}
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">
                                {scanning
                                    ? t("push.searchingDevices")
                                    : t("push.scanHint")}
                            </p>
                            <button
                                type="button"
                                onClick={() => scanDevices()}
                                disabled={scanning}
                                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
                                title={t("push.rescan")}
                            >
                                <RefreshCw
                                    size={14}
                                    className={cn(scanning && "animate-spin")}
                                />
                            </button>
                        </div>

                        {/* Scanning indicator — distinct radar animation */}
                        {scanning && discoveredDevices.length === 0 ? (
                            <div className="flex flex-col items-center py-6 gap-3">
                                <div className="relative w-10 h-10 flex items-center justify-center">
                                    <Wifi size={20} className="text-primary relative z-10" />
                                    <div className="absolute inset-0 rounded-full border-2 border-primary/30 animate-ping" />
                                    <div
                                        className="absolute inset-1 rounded-full border border-primary/20 animate-ping"
                                        style={{ animationDelay: "0.5s" }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {t("push.searchingLan")}
                                </p>
                            </div>
                        ) : null}

                        {/* Scanning with devices already found — subtle inline indicator */}
                        {scanning && discoveredDevices.length > 0 ? (
                            <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                                <Wifi size={12} className="text-primary animate-pulse" />
                                <p className="text-[11px] text-muted-foreground">{t("push.continueScanning")}</p>
                            </div>
                        ) : null}

                        {/* Discovered device list */}
                        {discoveredDevices.length > 0 ? (
                            <div className="space-y-1">
                                {discoveredDevices.map((device) => {
                                    const isConnecting = connectingDeviceId === device.deviceId;
                                    return (
                                        <button
                                            key={device.deviceId}
                                            type="button"
                                            onClick={() => handleSelectDevice(device)}
                                            disabled={connectingDeviceId !== null}
                                            className={cn(
                                                "w-full flex items-center gap-3 p-2.5 rounded-md transition-colors text-left",
                                                isConnecting
                                                    ? "bg-primary/10 border border-primary/20"
                                                    : connectingDeviceId !== null
                                                        ? "opacity-40 cursor-not-allowed"
                                                        : "hover:bg-muted/80 group",
                                            )}
                                        >
                                            {isConnecting ? (
                                                <Loader2
                                                    size={16}
                                                    className="text-primary animate-spin shrink-0"
                                                />
                                            ) : (
                                                <PlatformIcon platform={device.platform} />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-foreground truncate">
                                                    {device.deviceName}
                                                </div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    {isConnecting
                                                        ? t("push.connecting")
                                                        : `${device.address}:${device.port} · ${device.platform}`}
                                                </div>
                                            </div>
                                            {!isConnecting && connectingDeviceId === null && (
                                                <Send
                                                    size={12}
                                                    className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : null}

                        {/* No devices found */}
                        {!scanning && discoveredDevices.length === 0 ? (
                            <div className="text-center py-4">
                                <Wifi
                                    size={20}
                                    className="mx-auto text-muted-foreground mb-2"
                                />
                                <p className="text-xs text-muted-foreground mb-3">
                                    {t("push.noDevicesFound")}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => scanDevices()}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                                >
                                    <Search size={12} />
                                    {t("push.rescan")}
                                </button>
                            </div>
                        ) : null}

                        {connectError ? (
                            <p
                                className="text-xs theme-accent-text"
                                style={getThemeAccentStyle("error")}
                            >
                                {connectError}
                            </p>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}
